import useSWR from "swr";
import { swrFetcher, type AuditEntry } from "@/lib/api";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { formatTimestamp } from "@/lib/format";

function severityVariant(s: string): BadgeProps["variant"] {
  if (s === "critical") return "danger";
  if (s === "warn") return "warning";
  return "neutral";
}

export function AuditTab() {
  const { data, isLoading } = useSWR<{ items: AuditEntry[]; total: number }>(
    "/api/admin/audit?limit=50",
    swrFetcher,
    { refreshInterval: 10000 }
  );

  if (isLoading || !data) return <p className="text-sm text-studio-muted">Loading audit log…</p>;

  return (
    <div>
      <h1 className="mb-5 font-display text-xl font-bold tracking-tight sm:mb-6 sm:text-2xl">Audit Log</h1>
      {data.items.length === 0 ? (
        <p className="text-sm text-studio-muted">No audit entries yet.</p>
      ) : (
        <div className="space-y-2">
          {data.items.map((e) => (
            <div
              key={e.id}
              className="flex flex-col items-start gap-2 rounded-xl border border-studio-border bg-studio-card p-3 sm:flex-row sm:gap-3"
            >
              <Badge variant={severityVariant(e.severity)} className="mt-0.5 shrink-0">
                {e.severity}
              </Badge>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-studio-ink">{e.action}</p>
                <p className="break-all font-mono text-xs text-studio-faint sm:break-normal">
                  {e.resource_type && `${e.resource_type}:${e.resource_id} · `}
                  {formatTimestamp(e.created_at)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
      {data.total > 50 && (
        <p className="mt-4 text-xs text-studio-faint">Showing 50 of {data.total} entries</p>
      )}
    </div>
  );
}
